'use strict';

/**
 * Catalogue public : carrousels image sur les cartes (progressive enhancement).
 * Sans JS ou avec une seule image, le rendu MPA reste inchangé.
 */
function initCarousel(carousel) {
  const track = carousel.querySelector('.vehicle-catalog__card-carousel-track');
  if (!track) {
    return;
  }

  const slides = [...track.querySelectorAll('.vehicle-catalog__card-image')];
  if (slides.length <= 1) {
    return;
  }

  const prev = carousel.querySelector('[data-carousel-prev]');
  const next = carousel.querySelector('[data-carousel-next]');
  const counter = carousel.querySelector('[data-carousel-counter]');
  if (!prev || !next) {
    return;
  }

  let index = slides.findIndex((el) => el.classList.contains('vehicle-catalog__card-image--active'));
  if (index < 0) {
    index = 0;
  }

  const setIndex = (nextIndex) => {
    const n = slides.length;
    const i = ((nextIndex % n) + n) % n;
    index = i;
    slides.forEach((img, j) => {
      img.classList.toggle('vehicle-catalog__card-image--active', j === i);
    });
    if (counter) {
      counter.textContent = `${i + 1} / ${n}`;
    }
  };

  setIndex(index);

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  prev.addEventListener('click', (e) => {
    stop(e);
    setIndex(index - 1);
  });
  next.addEventListener('click', (e) => {
    stop(e);
    setIndex(index + 1);
  });
}

/**
 * Aligne l’état visuel des onglets type / réservations sur l’URL / data-* (progressive enhancement).
 * Corrige les écarts SSR tout en conservant les bons liens href.
 * Classes actives : public (`vehicle-catalog__type-tab--active`) + partner (`tc-partner-catalog-search__tab--active`).
 */
function initTypeTabsFromUrl(section) {
  const params = new URLSearchParams(window.location.search);
  const disponibiliteRaw = params.get('filters[disponibilite]') || '';
  const disponibiliteParts = disponibiliteRaw.split(',').map((part) => part.trim()).filter(Boolean);
  // Priorité onglet actif : reserved > consigned > type.
  const reservationsActive = disponibiliteParts.includes('reserved')
    || section.getAttribute('data-vehicle-catalog-reservations-active') === '1';
  const consignmentsActive = !reservationsActive && (
    disponibiliteParts.includes('consigned')
    || section.getAttribute('data-vehicle-catalog-consignments-active') === '1'
  );

  const vo = section.querySelector('#catalog-tab-voiture');
  const ut = section.querySelector('#catalog-tab-utilitaire');
  const res = section.querySelector('#catalog-tab-reservations');
  const cons = section.querySelector('#catalog-tab-consignments');
  const tabs = [vo, ut, res, cons].filter(Boolean);
  const activeClasses = [
    'vehicle-catalog__type-tab--active',
    'tc-partner-catalog-search__tab--active',
  ];

  tabs.forEach((a) => {
    activeClasses.forEach((className) => a.classList.remove(className));
    a.removeAttribute('aria-current');
  });

  let active = null;
  if (reservationsActive && res) {
    active = res;
  } else if (consignmentsActive && cons) {
    active = cons;
  } else {
    let type = params.get('filters[type]');
    if (type !== 'utilitaire' && type !== 'voiture') {
      type = section.getAttribute('data-vehicle-catalog-active-type') || 'voiture';
    }
    active = type === 'utilitaire' ? ut : vo;
  }

  if (active) {
    activeClasses.forEach((className) => active.classList.add(className));
    active.setAttribute('aria-current', 'page');
  }
}

const PRICE_FILTER_SELECTOR = '[data-vehicle-catalog-price-filter]';

/**
 * Helpers caret / live entier (copie fidèle de swiss-number-format.mjs — pas d'import
 * cross-package TC.Extranet).
 */
const PRICE_FILTER_SEPARATOR_REGEX = /['\u2019\u0020\u00A0\u202F\u2009]/g;

/**
 * @param {string} char
 * @returns {boolean}
 */
function isPriceFilterSignificantChar(char) {
  // Entiers : seuls les chiffres comptent pour le caret (pas de décimal live).
  return char >= '0' && char <= '9';
}

/**
 * @param {string} value
 * @param {number} caretPos
 * @returns {number}
 */
function countPriceFilterSignificantBeforeCaret(value, caretPos) {
  const text = String(value ?? '');
  const end = Math.max(0, Math.min(Number(caretPos) || 0, text.length));
  let count = 0;
  for (let i = 0; i < end; i += 1) {
    if (isPriceFilterSignificantChar(text.charAt(i))) {
      count += 1;
    }
  }
  return count;
}

/**
 * @param {string} formattedValue
 * @param {number} significantCount
 * @returns {number}
 */
function mapPriceFilterCaretAfterFormat(formattedValue, significantCount) {
  const text = String(formattedValue ?? '');
  const target = Math.max(0, Number(significantCount) || 0);
  if (target === 0) {
    return 0;
  }
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (isPriceFilterSignificantChar(text.charAt(i))) {
      seen += 1;
      if (seen >= target) {
        return i + 1;
      }
    }
  }
  return text.length;
}

/**
 * Formatage live entier (filtres prix). null = invalide → laisser inchangé.
 *
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
function formatPriceFilterLive(raw) {
  if (raw === null || raw === undefined) {
    return null;
  }
  const trimmed = String(raw).trim();
  if (trimmed === '') {
    return '';
  }
  const cleaned = trimmed.replace(PRICE_FILTER_SEPARATOR_REGEX, '');
  // Point / virgule : ne pas corriger silencieusement (contrat filtre ≠ money).
  if (/[.,]/.test(cleaned) || !/^\d+$/.test(cleaned)) {
    return null;
  }
  return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, '\'');
}

/**
 * Valeur numérique envoyée au GET (francs entiers, sans séparateur).
 * Historique : invalide → '' (divergence assumée vs money « laisser inchangé »).
 *
 * @param {string} raw
 * @returns {string}
 */
function parsePriceFilterInput(raw) {
  // Contrat de glyphes suisses : apostrophe droite/typographique + espaces
  // normal/insécable/fine insécable/fine, virgule décimale normalisée.
  const cleaned = String(raw ?? '')
    .trim()
    .replace(/['\u2019]/g, '')
    .replace(/[\s\u00A0\u202F\u2009]/g, '')
    .replace(/,/g, '.');
  if (cleaned === '') {
    return '';
  }
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) {
    return '';
  }

  return String(Math.round(value));
}

/**
 * Affichage type prix catalogue CH (séparateur milliers apostrophe, sans « CHF »).
 * Apostrophe explicite (pas d'espace insécable issu de toLocaleString).
 *
 * @param {string} raw
 * @returns {string}
 */
function formatPriceFilterDisplay(raw) {
  const normalized = parsePriceFilterInput(raw);
  if (normalized === '') {
    return '';
  }

  return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, '\'');
}

/**
 * @param {HTMLFormElement} form
 */
function normalizePriceFiltersBeforeSubmit(form) {
  form.querySelectorAll(PRICE_FILTER_SELECTOR).forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.value = parsePriceFilterInput(input.value);
    }
  });
}

/**
 * @param {HTMLInputElement} input
 */
function applyPriceFilterLive(input, event) {
  if (input.dataset.priceFilterComposing === '1') {
    return;
  }
  if (event && event.isComposing) {
    return;
  }
  const previous = input.value;
  const caretPos = input.selectionStart ?? previous.length;
  const significantCount = countPriceFilterSignificantBeforeCaret(previous, caretPos);
  const formatted = formatPriceFilterLive(previous);
  if (formatted === null || formatted === previous) {
    return;
  }
  input.value = formatted;
  const nextCaret = mapPriceFilterCaretAfterFormat(formatted, significantCount);
  try {
    input.setSelectionRange(nextCaret, nextCaret);
  } catch (_error) {
    // Ignore.
  }
}

/**
 * @param {HTMLElement} section
 */
function initPriceFilterInputs(section) {
  section.querySelectorAll(PRICE_FILTER_SELECTOR).forEach((input) => {
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    if (input.dataset.priceFilterBound === '1') {
      return;
    }
    input.dataset.priceFilterBound = '1';

    // Affichage initial seulement si entier valide (pas d'arrondi silencieux de décimales).
    if (input.value.trim() !== '' && formatPriceFilterLive(input.value) !== null) {
      input.value = formatPriceFilterDisplay(input.value);
    }

    input.addEventListener('compositionstart', () => {
      input.dataset.priceFilterComposing = '1';
    });
    input.addEventListener('compositionend', () => {
      input.dataset.priceFilterComposing = '0';
      applyPriceFilterLive(input);
    });

    input.addEventListener('input', (event) => applyPriceFilterLive(input, event));

    input.addEventListener('blur', () => {
      if (input.value.trim() === '') {
        return;
      }
      // Si la valeur contient un décimal (invalide pour filtre entier), ne pas arrondir.
      if (formatPriceFilterLive(input.value) === null) {
        return;
      }
      const normalized = parsePriceFilterInput(input.value);
      if (normalized !== '') {
        input.value = formatPriceFilterDisplay(normalized);
      }
    });
    // Focus : pas de dégroupage — valeur reste groupée.
  });
}


function init(root) {
  const base = root && typeof root.querySelector === 'function' ? root : document;

  base.querySelectorAll('[data-vehicle-catalog-carousel]').forEach(initCarousel);

  const section = base.querySelector('[data-vehicle-catalog-root]');
  if (!section) {
    return;
  }

  initTypeTabsFromUrl(section);
  initPriceFilterInputs(section);
  initFilterAutoSubmit(section);
}

/**
 * Résout le type catalogue (GET) : priorité à l’URL courante, puis data-* SSR, puis champ caché.
 * Évite la régression « onglet Utilitaires → filtre marque → retour Voitures » si `filters[type]` n’était pas réémis.
 *
 * @param {HTMLElement} section
 * @param {HTMLFormElement} form
 * @returns {'voiture'|'utilitaire'}
 */
function resolveCatalogVehicleType(section, form) {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('filters[type]');
  if (fromQuery === 'utilitaire' || fromQuery === 'voiture') {
    return fromQuery;
  }
  const fromData = section.getAttribute('data-vehicle-catalog-active-type');
  if (fromData === 'utilitaire' || fromData === 'voiture') {
    return fromData;
  }
  const hidden = form.querySelector('#catalog-filter-type') || form.querySelector('input[name="filters[type]"]');
  const fromHidden = hidden && hidden.value;
  if (fromHidden === 'utilitaire' || fromHidden === 'voiture') {
    return fromHidden;
  }
  return 'voiture';
}

/**
 * @param {HTMLFormElement} form
 * @param {'voiture'|'utilitaire'} value
 */
function ensureCatalogVehicleTypeField(form, value) {
  let input = form.querySelector('#catalog-filter-type') || form.querySelector('input[name="filters[type]"]');
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'filters[type]';
    input.id = 'catalog-filter-type';
    form.insertBefore(input, form.firstChild);
  }
  input.value = value;
}

/**
 * Soumission GET immédiate sur change des selects (phase 2 wireframe).
 * Si la marque change, réinitialise le modèle pour cohérence avec l’URL avant envoi.
 */
function initFilterAutoSubmit(section) {
  const form = section.querySelector('[data-vehicle-catalog-filter-form]');
  if (!form || form.tagName !== 'FORM') {
    return;
  }

  section.querySelectorAll('[data-vehicle-catalog-auto-submit]').forEach((el) => {
    el.addEventListener('change', () => {
      if (el.id === 'catalog-filter-marque') {
        const modele = form.querySelector('#catalog-filter-modele');
        if (modele && !modele.disabled) {
          modele.value = '';
        }
      }
      ensureCatalogVehicleTypeField(form, resolveCatalogVehicleType(section, form));
      normalizePriceFiltersBeforeSubmit(form);
      form.submit();
    });
  });

  form.addEventListener('submit', () => {
    normalizePriceFiltersBeforeSubmit(form);
  });

  initAvailabilityMultiSelect(section, form);
}

/**
 * Disponibilites multi-selection:
 * - "Tous les statuts" (value "") efface la selection.
 * - clic normal = selection unique (remplace la precedente)
 * - Ctrl/Cmd/Shift + clic = ajoute/retire dans une selection multiple
 */
function initAvailabilityMultiSelect(section, form) {
  const hidden = form.querySelector('#catalog-filter-disponibilite');
  if (!(hidden instanceof HTMLInputElement)) {
    return;
  }

  const buttons = [...section.querySelectorAll('[data-status-value]')];
  if (buttons.length === 0) {
    return;
  }

  const readSet = () =>
    new Set(
      hidden.value
        .split(',')
        .map((x) => x.trim())
        // reserved / consigned : onglets dédiés, pas les boutons inventaire.
        .filter((x) => x.length > 0 && x !== 'reserved' && x !== 'consigned')
    );

  const syncUi = () => {
    const selected = readSet();
    buttons.forEach((btn) => {
      const value = btn.getAttribute('data-status-value') || '';
      const active = value === '' ? selected.size === 0 : selected.has(value);
      btn.classList.toggle('vehicle-catalog__availability-button--active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  buttons.forEach((btn) => {
    btn.addEventListener('click', (event) => {
      const value = btn.getAttribute('data-status-value') || '';
      const selected = readSet();
      if (value === '') {
        hidden.value = '';
      } else {
        const wantsMulti = event.ctrlKey || event.metaKey || event.shiftKey;
        if (!wantsMulti) {
          // Click normal: selection unique (evite l'accumulation involontaire).
          hidden.value = selected.has(value) && selected.size === 1 ? '' : value;
        } else {
          // Click avec modifieur: multi-selection explicite.
          if (selected.has(value)) {
            selected.delete(value);
          } else {
            selected.add(value);
          }
          hidden.value = [...selected].join(',');
        }
      }
      syncUi();
      ensureCatalogVehicleTypeField(form, resolveCatalogVehicleType(section, form));
      normalizePriceFiltersBeforeSubmit(form);
      form.submit();
    });
  });

  syncUi();
}

export default { init };
